import { useParams, Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, User } from "lucide-react";
import Footer from "@/components/Footer";
import { BLOG_POSTS } from "@/data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="font-heading text-4xl text-white">Article Not Found</h1>
          <Link to="/blog" className="text-[#D4AF37] text-sm">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="blog-post">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <Link to="/blog" className="text-xs tracking-[0.1em] uppercase text-zinc-400 hover:text-[#D4AF37] transition-colors flex items-center gap-1">
            <ArrowRight className="w-3 h-3 rotate-180" /> All Articles
          </Link>
        </div>
      </nav>

      <main className="pt-28 pb-24 px-6">
        <article className="max-w-3xl mx-auto">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex gap-2 mb-6">
              {post.tags.map((tag) => (
                <span key={tag} className="text-[9px] tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1 rounded-sm">{tag}</span>
              ))}
            </div>
            <div className="gold-line-left w-24" />
          </header>

          {/* Content */}
          <div className="prose-fyb space-y-6" data-testid="blog-post-content">
            {post.content.map((block, i) => {
              if (block.type === "p") return <p key={`${block.type}-${i}`} className="text-zinc-300 text-base leading-relaxed">{block.text}</p>;
              if (block.type === "h2") return <h2 key={`${block.type}-${i}`} className="font-heading text-2xl sm:text-3xl tracking-tight uppercase text-white mt-10 mb-2">{block.text}</h2>;
              if (block.type === "h3") return <h3 key={`${block.type}-${i}`} className="font-heading text-xl tracking-tight uppercase text-[#D4AF37] mt-8 mb-2">{block.text}</h3>;
              if (block.type === "quote") return (
                <blockquote key={`${block.type}-${i}`} className="border-l-2 border-[#D4AF37] pl-5 py-2 my-6 text-zinc-300 italic text-base leading-relaxed">
                  {block.text}
                </blockquote>
              );
              if (block.type === "list") return (
                <ul key={`list-${i}`} className="space-y-2 my-4">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-zinc-300 text-sm">
                      <ArrowRight className="w-3 h-3 text-[#D4AF37] mt-1.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
              return null;
            })}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-[#121212] border border-[#D4AF37]/30 p-8 rounded-sm text-center space-y-4">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Ready For More?</p>
            <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Get The Full Story</h3>
            <p className="text-zinc-400 text-sm max-w-md mx-auto">
              The Forty Year Bender by Chris Merlo. Pre-order now and get the complete CLARITY Protocol.
            </p>
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
              data-testid="blog-cta-preorder"
            >
              Pre-Order The Book <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-16">
            <h3 className="font-heading text-xl tracking-tight uppercase text-white mb-6">More Articles</h3>
            <div className="space-y-4">
              {BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3).map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="block border border-white/10 hover:border-[#D4AF37]/30 p-4 rounded-sm transition-colors group"
                >
                  <h4 className="font-heading text-lg tracking-tight uppercase text-white group-hover:text-[#D4AF37] transition-colors">{related.title}</h4>
                  <p className="text-zinc-500 text-xs mt-1">{related.readTime} read</p>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
